import { useState } from "react";
import { canPurchaseHere } from "../../app/inAppPurchase.js";
import { Eye, EyeOff } from "lucide-react";
import { Button, EmptyState, Field, Page, Picker, Spinner, type PickerOption } from "@cntrl/ui";
import { useIsPro } from "../../app/bandContext.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import {
  useFleet, useMyBunk, useUpdateVehicle, usePlaceBunk, useClearBunk, useSetBunkJunk,
  type FleetView, type VehicleView,
} from "../../app/hooks/useBuses.js";
import { bunkCode, bunkGroup, bunkSide, bunkTier, GROUP_LABEL, SIDE_LABEL, TIER_LABEL, type Side } from "./bunkGeometry.js";
import { FleetShoppingList } from "./BusStockPanel.js";
import styles from "./MobileBusesScreen.module.scss";

/**
 * ARTIST CNTRL PRO — the mobile ROAD editor for Buses (ART-148, plan §7.7). A tour manager on the road reassigns
 * bunks and fixes a door code from a phone; they do not restructure the fleet. So this is deliberately the narrow
 * slice of the desktop `BusesScreen`: per COACH, each bunk's occupant (a crew member, empty, or luggage) and the
 * coach facts (driver, phone, door/bay codes, Wi-Fi, lock note). NOT here, on purpose: adding or removing
 * vehicles, trucks, auto-fill, clear-bus, reordering — those stay on desktop.
 *
 * The GATE is in the screen, not just on the More row (the legacy M-NAV-16 lesson: a row is a door, the screen is
 * the lock). Pro, then the SERVED `canEditFleet` verdict — the exact party-based AV-or-TM predicate
 * (`core.is_band_party_mover`) — so a TM moved out of Production Office reads a plain statement rather than a 403
 * card, and a Production-Office PM who deep-links here gets the editor.
 *
 * WRITES ARE PER ACTION, NOT A SAVE-THE-WHOLE-BLOB bar (§7.7 "do not port that shape"). A bunk pick goes straight
 * to the server, which holds the three placement invariants (one bunk per person fleet-wide, an occupied target
 * swaps, an aboard person moves); the facts form saves one coach at a time under its `e_ver`, so a coach edited on
 * desktop mid-session conflicts loudly instead of being overwritten.
 */
export function MobileBusesScreen() {
  const isPro = useIsPro();
  const mine = useMyBunk(isPro);

  if (!isPro) {
    return (
      <Page title="Buses">
        <EmptyState
          title="Buses is an Artist CNTRL Pro feature"
          description={`Bunk assignments, driver, codes and Wi-Fi per coach.${canPurchaseHere() ? " Upgrade to Pro to run your fleet." : ""}`}
        />
      </Page>
    );
  }
  if (mine.isLoading) return <Page title="Buses"><Spinner /></Page>;
  if (!mine.data?.canEditFleet) {
    return (
      <Page title="Buses">
        <EmptyState
          title="The bus chart is managed by your tour manager"
          description="Your own bunk and the bus codes show on Day of Show and Show Logistics."
        />
      </Page>
    );
  }
  return <FleetEditor />;
}

function FleetEditor() {
  const fleet = useFleet();
  if (fleet.isLoading) return <Page title="Buses"><Spinner /></Page>;
  if (fleet.isError || !fleet.data) {
    return (
      <Page title="Buses">
        <EmptyState title="Couldn't load the fleet" description="Something went wrong loading the buses. Try again in a moment." />
      </Page>
    );
  }
  const coaches = fleet.data.vehicles.filter((v) => v.kind === "bus");
  if (coaches.length === 0) {
    return (
      <Page title="Buses">
        <EmptyState title="No buses in the fleet yet" description="Add one from the Buses screen on desktop." />
      </Page>
    );
  }
  return (
    <Page title="Buses">
      {coaches.map((v) => <CoachEditor key={v.vehicleId} v={v} crew={fleet.data.crew} />)}
      <FleetShoppingList />
    </Page>
  );
}

const JUNK = "__junk";
const EMPTY = "";

function CoachEditor({ v, crew }: { v: VehicleView; crew: FleetView["crew"] }) {
  const sides = (["D", "P"] as Side[]).filter((side) => v.bunks.some((b) => bunkSide(b.slot) === side));
  return (
    <section className={styles.coach} aria-label={v.name}>
      <header className={styles.head}>
        <h2 className={styles.name}>{v.name}</h2>
        {v.tag ? <span className={styles.tag}>{v.tag}</span> : null}
        <span className={styles.count}>{v.filled}/{v.capacity}</span>
      </header>

      {sides.map((side) => (
        <div key={side} className={styles.side}>
          <h3 className={styles.sideLabel}>{SIDE_LABEL[side]}</h3>
          {v.bunks
            .filter((b) => bunkSide(b.slot) === side)
            .map((b) => <BunkPicker key={b.slot} v={v} slot={b.slot} junk={b.junk} personId={b.personId} crew={crew} />)}
        </div>
      ))}

      <CoachFactsForm v={v} />
    </section>
  );
}

/** One bunk's occupant. A crew member already on another bunk is labelled with where they are, because picking
 *  them MOVES them (the server's invariant) — the label is what tells the TM that is about to happen. */
function BunkPicker({ v, slot, junk, personId, crew }: {
  v: VehicleView; slot: number; junk: boolean; personId: string | null; crew: FleetView["crew"];
}) {
  const place = usePlaceBunk();
  const clear = useClearBunk();
  const setJunk = useSetBunkJunk();
  const busy = place.isPending || clear.isPending || setJunk.isPending;

  const options: PickerOption[] = [
    { value: EMPTY, label: "— Empty —" },
    { value: JUNK, label: "Junk (luggage)" },
    ...crew.map((c) => {
      const elsewhere = c.assigned && !(c.assigned.vehicleId === v.vehicleId && c.personId === personId);
      return {
        value: c.personId,
        label: elsewhere ? `${c.displayName} · now ${c.assigned!.vehicleName} ${c.assigned!.code}` : c.displayName,
      };
    }),
  ];
  const value = junk ? JUNK : personId ?? EMPTY;

  const onChange = async (next: string) => {
    if (next === value) return;
    try {
      if (next === JUNK) {
        await setJunk.mutateAsync({ vehicleId: v.vehicleId, slot, junk: true });
      } else if (next === EMPTY) {
        if (junk) await setJunk.mutateAsync({ vehicleId: v.vehicleId, slot, junk: false });
        else await clear.mutateAsync({ vehicleId: v.vehicleId, slot });
      } else {
        // A luggage bunk refuses a person, so lift the luggage mark first.
        if (junk) await setJunk.mutateAsync({ vehicleId: v.vehicleId, slot, junk: false });
        await place.mutateAsync({ personId: next, vehicleId: v.vehicleId, slot });
      }
    } catch (e) {
      pushToast(e instanceof Error ? e.message : "Couldn't update the bunk.", "error");
    }
  };

  return (
    <div className={styles.bunk}>
      <Picker
        label={`${bunkCode(slot)} · ${GROUP_LABEL[bunkGroup(slot)]} ${TIER_LABEL[bunkTier(slot)].toLowerCase()}`}
        value={value}
        options={options}
        onChange={(n) => void onChange(n)}
        disabled={busy}
      />
    </div>
  );
}

type Facts = { driver: string; driverPhone: string; door: string; bay: string; wifi: string; wifiPassword: string; lockNote: string };
const factsOf = (v: VehicleView): Facts => ({
  driver: v.driver ?? "", driverPhone: v.driverPhone ?? "", door: v.door ?? "", bay: v.bay ?? "",
  wifi: v.wifi ?? "", wifiPassword: v.wifiPassword ?? "", lockNote: v.lockNote ?? "",
});

/** The coach facts, saved one coach at a time under its version. The codes and the Wi-Fi password are MASKED
 *  until the eye is pressed (PO, ART-148): the same posture as the read-side card, because a TM edits these in a
 *  crowded load-in where a shoulder-glance hands out bus access. `secureEntry` is the right mask here — a vendor
 *  secret typed on our origin that no password manager should offer or save (Field's own doc). */
function CoachFactsForm({ v }: { v: VehicleView }) {
  const update = useUpdateVehicle();
  const fleet = useFleet();
  const [f, setF] = useState<Facts>(() => factsOf(v));
  const [reveal, setReveal] = useState(false);
  const server = factsOf(v);
  const dirty = (Object.keys(f) as (keyof Facts)[]).some((k) => f[k] !== server[k]);
  const set = (k: keyof Facts) => (e: { target: { value: string } }) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = () => update.mutate(
    { vehicleId: v.vehicleId, version: v.version, body: { name: v.name, ...f, trailer: v.trailer, cargo: v.cargo } },
    {
      onSuccess: () => pushToast(`${v.name} saved`, "success"),
      onError: (e) => {
        if (e instanceof ApiError && e.status === 409) {
          // Someone saved this coach elsewhere. Reload it — the edits on screen are kept, so the TM can check
          // them against the fresh values and save again deliberately.
          void fleet.refetch();
          pushToast("This bus changed on another device — check the fields and save again.", "error");
        } else {
          pushToast(e.message || "Couldn't save the bus.", "error");
        }
      },
    },
  );

  return (
    <div className={styles.facts}>
      <div className={styles.factsHead}>
        <h3 className={styles.sideLabel}>Driver · codes · Wi-Fi</h3>
        <button
          type="button"
          className={styles.reveal}
          aria-pressed={reveal}
          aria-label={reveal ? "Hide codes" : "Show codes"}
          onClick={() => setReveal((r) => !r)}
        >
          {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
          {reveal ? "Hide codes" : "Show codes"}
        </button>
      </div>
      <Field label="Driver" value={f.driver} onChange={set("driver")} />
      <Field label="Driver phone" type="tel" value={f.driverPhone} onChange={set("driverPhone")} />
      <div className={styles.pair}>
        <Field label="Door code" value={f.door} onChange={set("door")} secureEntry={!reveal} revealToggle={false} antiAutofill />
        <Field label="Bay code" value={f.bay} onChange={set("bay")} secureEntry={!reveal} revealToggle={false} antiAutofill />
      </div>
      <Field label="Wi-Fi network" value={f.wifi} onChange={set("wifi")} />
      <Field label="Wi-Fi password" value={f.wifiPassword} onChange={set("wifiPassword")} secureEntry={!reveal} revealToggle={false} antiAutofill />
      <Field label="Lock note" value={f.lockNote} onChange={set("lockNote")} />
      {dirty ? (
        <div className={styles.saveRow}>
          <Button variant="ghost" onClick={() => setF(server)}>Discard</Button>
          <Button onClick={save} loading={update.isPending}>Save {v.name}</Button>
        </div>
      ) : null}
    </div>
  );
}
